import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { FeedEvent } from "@/lib/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedPage {
  data: FeedEvent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchFeed(page: number, pageSize: number): Promise<FeedPage> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // occurred_at desc + id como desempate: occurred_at pode colidir entre
  // linhas de fontes diferentes, id garante ordem estavel entre paginas.
  const { data, error, count } = await supabase
    .from("feed_events")
    .select("*", { count: "exact" })
    .order("occurred_at", { ascending: false })
    .order("id", { ascending: false })
    .range(from, to);

  if (error) throw error;

  const total = count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    data: (data ?? []) as FeedEvent[],
    total,
    page,
    pageSize,
    totalPages,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFeed(page: number, pageSize: number) {
  return useQuery<FeedPage, Error>({
    queryKey: ["feed-events", page, pageSize],
    queryFn: () => fetchFeed(page, pageSize),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: true,
  });
}
